import { Package, Pencil, UserPlus } from 'lucide-react';
import { STRINGS } from '@/strings/strings';

/**
 * Block 2 · 2.1 · Three-step permanent explainer at the top of the
 * Personen screen (Sebastian ruling 2026-09-09, revised 2026-09-24
 * for the one-question-at-a-time flow).
 *
 * ═══ ACTIVE VS RECEDED ════════════════════════════════════════════════
 *
 * Sebastian ruling 2026-09-24: the customer is on the Personen screen,
 * so tile 1 ("Personen anlegen") renders at full strength; the two
 * upcoming tiles ("Geschichte beschreiben", "Ansehen und bestellen")
 * visibly recede to ~60 % opacity so they read as "comes later"
 * rather than as disabled buttons. Card texts unchanged.
 *
 * ═══ MOBILE COMPACT MODE ══════════════════════════════════════════════
 *
 * Sebastian ruling 2026-09-24: on mobile, only the active tile is
 * visible; the two upcoming tiles collapse into one slim caption line
 * beneath. Two DOM subtrees are rendered — one `md:hidden` for the
 * mobile view, one `hidden md:grid` for tablet+. Screen readers on
 * real browsers respect `display:none`, so the desktop tile 2/3
 * copies are not announced when the customer is on a mobile viewport
 * (and vice versa for the mobile slim caption). Tests use
 * `getAllByText` where the same string can appear in both subtrees.
 */

const TILE_CLASS =
  'flex flex-col gap-2 rounded-2xl border border-book-border bg-book-card/60 p-4 md:p-5';
const TILE_RECEDED_CLASS = `${TILE_CLASS} opacity-60`;
const ICON_WRAP_CLASS =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-dream-gold/25 text-dream-navy';
const TITLE_CLASS = 'text-base font-semibold text-dream-navy';
const BODY_CLASS = 'text-sm text-book-text-secondary leading-relaxed';

export function DreiSchritte() {
  const s = STRINGS.book.personen.dreiSchritte;
  return (
    <section aria-labelledby="drei-schritte-heading" className="space-y-4">
      <h2 id="drei-schritte-heading" className="text-lg font-semibold text-dream-navy">
        {s.heading}
      </h2>

      {/* Mobile — active tile only, plus a slim caption for the two
          upcoming steps. Hidden on md+ where the desktop grid takes
          over. */}
      <div className="md:hidden">
        <div className={TILE_CLASS}>
          <div className={ICON_WRAP_CLASS} aria-hidden>
            <UserPlus className="h-5 w-5" strokeWidth={2} />
          </div>
          <p className={TITLE_CLASS}>{s.tile1Title}</p>
          <p className={BODY_CLASS}>{s.tile1Body}</p>
        </div>
        <p className="mt-2 px-1 text-xs text-book-text-secondary/70">
          {s.tile2Title} · {s.tile3Title}
        </p>
      </div>

      {/* Tablet + desktop — full three-tile grid, upcoming tiles at
          ~60 % opacity so they read as "comes later". */}
      <div className="hidden grid-cols-3 gap-4 md:grid">
        <div className={TILE_CLASS}>
          <div className={ICON_WRAP_CLASS} aria-hidden>
            <UserPlus className="h-5 w-5" strokeWidth={2} />
          </div>
          <p className={TITLE_CLASS}>{s.tile1Title}</p>
          <p className={BODY_CLASS}>{s.tile1Body}</p>
        </div>
        <div className={TILE_RECEDED_CLASS}>
          <div className={ICON_WRAP_CLASS} aria-hidden>
            <Pencil className="h-5 w-5" strokeWidth={2} />
          </div>
          <p className={TITLE_CLASS}>{s.tile2Title}</p>
          <p className={BODY_CLASS}>{s.tile2Body}</p>
        </div>
        <div className={TILE_RECEDED_CLASS}>
          <div className={ICON_WRAP_CLASS} aria-hidden>
            <Package className="h-5 w-5" strokeWidth={2} />
          </div>
          <p className={TITLE_CLASS}>{s.tile3Title}</p>
          <p className={BODY_CLASS}>{s.tile3Body}</p>
        </div>
      </div>
    </section>
  );
}
